// src/pages/AddShowtimes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE ?? "";

/* ------------------------------ shared input style ------------------------------ */
const INPUT_LIGHT =
  "bg-white/90 border border-white/40 text-slate-900 placeholder-slate-500 caret-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-xl";

/* ------------------------------- fetch utils ------------------------------ */
async function getJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${path}${t ? ` — ${t}` : ""}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}
const pad2 = (n) => String(n).padStart(2, "0");

/* ------------------------------ schema helpers ---------------------------- */
const normHall = (h) => ({
  hall_id: String(h?.hall_id ?? h?.HALL_ID ?? h?.id ?? ""),
  hall_name: h?.hall_name ?? h?.HALL_NAME ?? h?.name ?? "",
  cinema_id: String(h?.cinema_id ?? h?.CINEMA_ID ?? h?.cinema ?? h?.cinemaId ?? ""),
});

/* ------------------------------ fuzzy helpers ----------------------------- */
const STOP_BG = new Set(["то","та","те","ът","ите","на","в","с","и","за","по","от","до","или","но","без","че","както"]);
const STRIP_TAGS = new Set(["3d","2d","4dx","imax","dub","sub","дублаж","субтитри","bg","eng","vo","ov","legend"]);
const DIGIT_TO_ROMAN = { "2":"ii","3":"iii","4":"iv","5":"v","6":"vi","7":"vii","8":"viii","9":"ix" };

const normalizeTitle = (s) => {
  const base = String(s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[()"/'.,:;!?+–—\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = base.split(" ").filter(Boolean);
  const filtered = [];
  for (let w of words) {
    if (STOP_BG.has(w)) continue;
    if (STRIP_TAGS.has(w)) continue;
    if (DIGIT_TO_ROMAN[w]) filtered.push(DIGIT_TO_ROMAN[w]);
    filtered.push(w);
  }
  return filtered.join(" ");
};
const levRatio = (a, b) => {
  a = a || ""; b = b || "";
  if (a === b) return 1;
  const m = a.length, n = b.length;
  if (!m || !n) return 0;
  const dp = Array(n + 1).fill(0).map((_, j) => j);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = Math.min(
        dp[j] + 1,
        dp[j - 1] + 1,
        prev + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      prev = tmp;
    }
  }
  const dist = dp[n];
  return 1 - dist / Math.max(m, n);
};
const trigrams = (s) => {
  const t = [];
  const x = `  ${s} `;
  for (let i = 0; i < x.length - 2; i++) t.push(x.slice(i, i + 3));
  return t;
};
const diceTrigram = (a, b) => {
  const A = trigrams(a), B = trigrams(b);
  if (!A.length || !B.length) return 0;
  const map = new Map();
  for (const g of B) map.set(g, (map.get(g) || 0) + 1);
  let inter = 0;
  for (const g of A) {
    const c = map.get(g);
    if (c) { inter++; map.set(g, c - 1); }
  }
  return (2 * inter) / (A.length + B.length);
};
const scoreTitle = (aRaw, bRaw) => {
  const a = normalizeTitle(aRaw), b = normalizeTitle(bRaw);
  if (!a || !b) return 0;
  if (a === b) return 100;
  const aw = new Set(a.split(" ")), bw = new Set(b.split(" "));
  let tokenScore = 0; for (const w of aw) if (bw.has(w)) tokenScore += 6;
  const sub = (a.includes(b) || b.includes(a)) ? 30 : 0;
  const dice = diceTrigram(a, b) * 40;
  const lev = levRatio(a, b) * 30;
  return Math.min(100, tokenScore + sub + dice + lev);
};

/* -------------------------------- component ------------------------------- */
export default function AddShowtimes({ onlyCinemaId = null, hideBack = false }) {
  const { movieId } = useParams();
  const navigate = useNavigate();

  // base
  const [cinemas, setCinemas] = useState([]);
  const [halls, setHalls] = useState([]);
  const [movies, setMovies] = useState([]);

  // selections
   const [selectedCinema, setSelectedCinema] = useState(
       onlyCinemaId ? String(onlyCinemaId) : ""
     );
  const [selectedMovie, setSelectedMovie] = useState(movieId || "");
  const [selectedHall, setSelectedHall] = useState("");

  // filtering/search
  const [movieSearch, setMovieSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");

  // quick add
  const [dateTimeRaw, setDateTimeRaw] = useState("");
  const dateInputRef = useRef(null);

  // data
  const [movieDetails, setMovieDetails] = useState(null);
  const [showtimes, setShowtimes] = useState([]);

  // edit metadata for existing showtimes (admin table)
  const [metaEdits, setMetaEdits] = useState({});
  const [metaSavingId, setMetaSavingId] = useState(null);

  const updateMetaEdit = (s, patch) => {
    const id = String(s.showtime_id);
    setMetaEdits((prev) => {
      const base = prev[id] ?? {
        format_type: s.format_type || "",
        is_hfr: Number(s.is_hfr) ? 1 : 0,
        has_dub: String(s.sub_type || "").toUpperCase() === "DUB" || String(s.audio_lang || "").toUpperCase() === "BG",
        has_sub: String(s.sub_type || "").toUpperCase() === "SUB" || String(s.sub_lang || "").toUpperCase() === "BG" || Number(s.is_ocap) === 1,
        is_ocap: Number(s.is_ocap) ? 1 : 0,
        import_price: (s.import_price ?? "") === null ? "" : String(s.import_price ?? "")
      };
      return { ...prev, [id]: { ...base, ...patch } };
    });
  };

  const saveShowtimeMeta = async (s) => {
    const id = String(s.showtime_id);
    const e = metaEdits[id] ?? {
      format_type: s.format_type || "",
      is_hfr: Number(s.is_hfr) ? 1 : 0,
      has_dub: String(s.sub_type || "").toUpperCase() === "DUB" || String(s.audio_lang || "").toUpperCase() === "BG",
      has_sub: String(s.sub_type || "").toUpperCase() === "SUB" || String(s.sub_lang || "").toUpperCase() === "BG" || Number(s.is_ocap) === 1,
      is_ocap: Number(s.is_ocap) ? 1 : 0,
      import_price: (s.import_price ?? "") === null ? "" : String(s.import_price ?? "")
    };

    setMetaSavingId(id);
    try {
      const hasSub = e.is_ocap ? true : !!e.has_sub;
      const hasDub = !!e.has_dub;

      const payload = {
        format_type: e.format_type || null,
        is_hfr: e.is_hfr ? 1 : 0,
        // Keep SUB_TYPE for back-compat, but also send explicit flags
        sub_type: hasDub ? "DUB" : (hasSub ? "SUB" : null),
        audio_lang: hasDub ? "BG" : null,
        sub_lang: hasSub ? "BG" : null,
        is_ocap: e.is_ocap ? 1 : 0,
        import_price: e.import_price === "" ? null : Number(String(e.import_price).replace(",", "."))
      };
      const res = await fetch(`${API}/api/showtimes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Save failed");
      setMetaEdits((prev) => {
        const cp = { ...prev };
        delete cp[id];
        return cp;
      });
      await refreshShowtimes();
    } catch (err) {
      alert("❌ Грешка при запис на типа.");
    } finally {
      setMetaSavingId(null);
    }
  };

  // paste/import

  const [pasteText, setPasteText] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  /* ------------------------------- load base ------------------------------ */
  useEffect(() => {
       (async () => {
         const [cin, hal, mov] = await Promise.all([
           getJSON("/api/cinemas"),
           getJSON("/api/halls").catch(() => []),
           getJSON("/api/movies"),
         ]);
    
         let cinemasData = Array.isArray(cin) ? cin : [];
    
         // If we come from POS – lock to that cinema only
        if (onlyCinemaId) {
           const idStr = String(onlyCinemaId);
           cinemasData = cinemasData.filter(
             (c) => String(c.cinema_id ?? c.CINEMA_ID) === idStr
           );
           setSelectedCinema(idStr);
         }
    
         setCinemas(cinemasData);
         setHalls(Array.isArray(hal) ? hal.map(normHall) : []);
         setMovies(Array.isArray(mov) ? mov : []);
    
         if (movieId && Array.isArray(mov)) {
           const found = mov.find((m) => String(m.MOVIE_ID) === String(movieId));
           setMovieDetails(found || null);
           if (found) setSelectedMovie(String(found.MOVIE_ID));
         }
       })().catch(console.error);
     }, [movieId, onlyCinemaId]);

  // Optional: pull halls for selected cinema (if endpoint exists)
  useEffect(() => {
    (async () => {
      if (!selectedCinema) return;
      try {
        const per = await getJSON(`/api/halls/${selectedCinema}`);
        if (Array.isArray(per) && per.length) {
          const perNorm = per.map(normHall);
          setHalls((old) => {
            const others = old.filter((h) => h.cinema_id !== String(selectedCinema));
            return [...others, ...perNorm];
          });
        }
      } catch {}
    })();
  }, [selectedCinema]);

  const refreshShowtimes = async (cinId = selectedCinema, movId = selectedMovie) => {
    if (!cinId || !movId) { setShowtimes([]); return; }
    try {
      const data = await getJSON(`/api/showtimes/${cinId}?movie_id=${movId}`);
      setShowtimes(Array.isArray(data) ? data : []);
    } catch { setShowtimes([]); }
  };
  useEffect(() => { refreshShowtimes(); }, [selectedCinema, selectedMovie]);

  /* ------------------------------- derived -------------------------------- */
  const hallsForCinema = useMemo(
    () => halls.filter((h) => h.cinema_id === String(selectedCinema)),
    [halls, selectedCinema]
  );

  const availableDates = useMemo(() => {
    const list = showtimes.map((s) => (s.show_date || "").split("T")[0]).filter(Boolean);
    return Array.from(new Set(list));
  }, [showtimes]);

  /* ------------------------------ quick add ------------------------------- */
  const handleAddShowtime = async () => {
    if (!selectedCinema || !selectedMovie || !selectedHall || !/^\d{8}$/.test(dateTimeRaw)) {
      alert("❌ Попълнете всички полета. Формат: DDMMHHMM (напр. 15081930).");
      return;
    }
    const body = [{
      cinema_id: Number(selectedCinema),
      hall: Number(selectedHall),
      movie: Number(selectedMovie),
      dateTimeRaw,
    }];
    try {
      await getJSON("/api/showtimes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setDateTimeRaw("");
      dateInputRef.current?.focus();
      await refreshShowtimes();
    } catch { alert("❌ Неуспешно добавяне."); }
  };

  /* ---------------------------- parse pasted text ------------------------- */
  const movieIdByName = useMemo(() => {
    const map = new Map();
    for (const m of movies) map.set(m.MOVIE_NAME, String(m.MOVIE_ID));
    return map;
  }, [movies]);

  const parsePaste = () => {
    setPreviewOpen(true);
    let raw = (pasteText || "").replace(/\u00a0/g, " ").replace(/\r/g, "\n");
    raw = raw.replace(/[ \t]+\n/g, "\n").trim();
    if (!raw) { setPreviewRows([]); return; }

    const lines = raw.split(/\n+/);
    const out = [];
    let curDate = null;

    const dateRx = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/i;
    const timeTitleRx = /^\s*(\d{1,2})[.:](\d{2})\s*(?:ч\.?)?\s+(.*)$/i;

    const db = movies.map((m) => ({ id: String(m.MOVIE_ID), name: m.MOVIE_NAME, norm: normalizeTitle(m.MOVIE_NAME) }));

    for (let line of lines) {
      line = line.trim();
      if (!line) continue;

      const dm = line.match(dateRx);
      if (dm) {
        const yyyy = dm[3], mm = pad2(dm[2]), dd = pad2(dm[1]);
        curDate = `${yyyy}-${mm}-${dd}`;
        continue;
      }
      if (!curDate) continue;

      const m = line.match(timeTitleRx);
      if (!m) continue;

      const hh = pad2(m[1]), mm = m[2];
      const titleRaw = m[3].trim();

      const nRaw = normalizeTitle(titleRaw);
      let best = { id: null, name: "__new__", score: 0 };
      for (const rec of db) {
        const sc = scoreTitle(nRaw, rec.norm);
        if (sc > best.score) best = { id: rec.id, name: rec.name, score: sc };
      }
      const matchName = best.score >= 35 ? best.name : "__new__";
      const matchId = matchName === "__new__" ? null : best.id;

      out.push({ date: curDate, time: `${hh}:${mm}`, original: titleRaw, matchName, matchId, score: best.score });
    }
    setPreviewRows(out);
  };

  const changeMatch = (idx, val) => {
    setPreviewRows((prev) => {
      const cp = [...prev];
      cp[idx] = { ...cp[idx], matchName: val, matchId: val === "__new__" ? null : (movieIdByName.get(val) || null), score: val === "__new__" ? 0 : cp[idx].score };
      return cp;
    });
  };

  const importParsed = async () => {
    if (!selectedCinema || !selectedHall) {
      alert("Моля, изберете кино и зала за импорта.");
      return;
    }
    const ready = previewRows.filter((r) => r.matchId);
    if (!ready.length) { alert("Няма редове със съвпадение."); return; }

    const payload = ready.map((r) => {
      const [yyyy, mm, dd] = r.date.split("-");
      const [HH, MM] = r.time.split(":");
      const dateTimeRaw = `${pad2(dd)}${pad2(mm)}${pad2(HH)}${pad2(MM)}`;
      return {
        cinema_id: Number(selectedCinema),
        hall: Number(selectedHall),
        movie: Number(r.matchId),
        dateTimeRaw,
      };
    });

    try {
      setImportBusy(true);
      await getJSON("/api/showtimes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setImportBusy(false);
      setPreviewRows([]);
      setPasteText("");
      setPreviewOpen(false);
      await refreshShowtimes();
      alert(`✅ Импортирани прожекции: ${payload.length}`);
    } catch (e) {
      console.error(e);
      setImportBusy(false);
      alert("❌ Импортът неуспешен.");
    }
  };

  /* --------------------------------- render -------------------------------- */
  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]">
      {/* Фон с пурпурен воал */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

            {/* Хедър */}
            <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 mb-4">
        {!hideBack && (
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-2 ... "
            title="Назад"
          >
            ← Назад
          </button>
        )}
        <div className="flex items-center gap-3">

          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="Лого"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
            📅 Прожекции — Администрация
          </h1>
        </div>
      </div>

      {/* Карта */}
      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* Топ селектори */}
        <div className="flex flex-wrap gap-3 mb-6">
        <select
            value={selectedCinema}
            onChange={(e) => { setSelectedCinema(e.target.value); setSelectedHall(""); }}
            className={`px-3 py-2 ${INPUT_LIGHT}`}
            disabled={!!onlyCinemaId}
          >
            {!onlyCinemaId && <option value="">🎥 Изберете кино</option>}
            {cinemas.map((c) => (
              <option key={c.cinema_id} value={String(c.cinema_id)}>
                {c.cinema_name} {c.city ? `(${c.city})` : ""}
              </option>
            ))}
          </select>


          <input
            type="text"
            placeholder="🔍 Търси филм…"
            className={`px-3 py-2 ${INPUT_LIGHT}`}
            value={movieSearch}
            onChange={(e) => setMovieSearch(e.target.value)}
          />

          <select
            value={selectedMovie}
            onChange={(e) => setSelectedMovie(e.target.value)}
            className={`px-3 py-2 ${INPUT_LIGHT}`}
          >
            <option value="">🎞 Изберете филм</option>
            {movies
              .filter((m) => m.MOVIE_NAME?.toLowerCase().includes(movieSearch.toLowerCase()))
              .sort((a, b) => a.MOVIE_NAME.localeCompare(b.MOVIE_NAME, "bg"))
              .map((m) => (
                <option key={m.MOVIE_ID} value={String(m.MOVIE_ID)}>{m.MOVIE_NAME}</option>
              ))}
          </select>

          <select value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className={`px-3 py-2 ${INPUT_LIGHT}`}>
            <option value="">📆 Всички дати</option>
            {availableDates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        {/* Бързо добавяне */}
        <div className="rounded-2xl border border-white/15 bg-white/5 p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">➕ Нова прожекция</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={selectedHall}
              onChange={(e) => setSelectedHall(e.target.value)}
              className={`w-44 px-3 py-2 ${INPUT_LIGHT}`}
            >
              <option value="">🏟 Зала</option>
              {hallsForCinema.map((h) => (
                <option key={`${h.cinema_id}-${h.hall_id}`} value={h.hall_id}>
                  {h.hall_name}
                </option>
              ))}
            </select>

            <input
              ref={dateInputRef}
              type="text"
              value={dateTimeRaw}
              onChange={(e) => setDateTimeRaw(e.target.value)}
              placeholder="напр. 15081930 → 15.08 19:30"
              className={`w-52 px-3 py-2 ${INPUT_LIGHT}`}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddShowtime(); }}
            />

            <button
              onClick={handleAddShowtime}
              className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white border border-white/10"
            >
              💾 Добави
            </button>
          </div>
        </div>

        {/* Импорт от текст/Excel */}
        <div className="rounded-2xl border border-white/15 bg-white/5 p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">📋 Импорт от Excel / Word</h2>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Постави съдържанието (пример като картинката)</label>
              <textarea
                rows={12}
                className="w-full p-3 font-mono rounded-xl bg-white/90 border border-white/40 text-slate-900 placeholder-slate-500 caret-slate-900"
                placeholder={`15.08.2025 г.
9.00   ПРИКЛЮЧЕНИЕТО НА ДЖОУИ
10:30 ч. ПЪТЕШЕСТВИЕ В ДИВИЯ ЗАПАД
...`}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <div className="flex gap-2 mt-2">
                <button className="px-3 py-1.5 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15" onClick={parsePaste}>🔍 Визуализация</button>
                <button className="px-3 py-1.5 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15" onClick={() => { setPasteText(""); setPreviewRows([]); setPreviewOpen(false); }}>🧹 Изчисти</button>
              </div>
            </div>

            <div className="w-full lg:w-80">
              <label className="block text-sm font-medium mb-1">Кино и Зала за импорта</label>
              <select
                value={selectedCinema}
                onChange={(e) => { setSelectedCinema(e.target.value); setSelectedHall(""); }}
                className={`mb-2 w-full px-3 py-2 ${INPUT_LIGHT}`}
                disabled={!!onlyCinemaId}
              >
                {!onlyCinemaId && <option value="">🎥 Изберете кино</option>}
                {cinemas.map((c) => (
                  <option key={c.cinema_id} value={String(c.cinema_id)}>
                    {c.cinema_name} {c.city ? `(${c.city})` : ""}
                  </option>
                ))}
              </select>

              <select
                value={selectedHall}
                onChange={(e) => setSelectedHall(e.target.value)}
                className={`w-full px-3 py-2 ${INPUT_LIGHT}`}
              >
                <option value="">🏟 Зала</option>
                {hallsForCinema.map((h) => (
                  <option key={`${h.cinema_id}-${h.hall_id}`} value={h.hall_id}>{h.hall_name}</option>
                ))}
              </select>

              {previewOpen && (
                <button
                  className="mt-3 w-full px-3 py-2 rounded-xl bg-emerald-600 text-white disabled:opacity-60"
                  onClick={importParsed}
                  disabled={importBusy || !previewRows.some((r) => r.matchId)}
                >
                  {importBusy ? "⏳ Импортиране..." : `✅ Импортирай (${previewRows.filter((r) => r.matchId).length})`}
                </button>
              )}
              <div className="text-sm text-white/80 mt-2">
                • Редове с <em>Ново заглавие</em> няма да се импортират. Изберете филм от падащото меню.<br/>
                • Съвпаденията под ~35 точки се маркират в жълто за проверка.
              </div>
            </div>
          </div>

          {previewOpen && (
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm rounded-xl overflow-hidden">
                <thead className="bg-white/10 text-white">
                  <tr>
                    <th className="p-2 border border-white/10">Дата</th>
                    <th className="p-2 border border-white/10">Час</th>
                    <th className="p-2 border border-white/10">Оригинал</th>
                    <th className="p-2 border border-white/10">Съвпадение (от база)</th>
                    <th className="p-2 border border-white/10">Оценка</th>
                    <th className="p-2 border border-white/10">Статус</th>
                  </tr>
                </thead>
                <tbody className="bg-white/5 text-white">
                  {!previewRows.length && <tr><td className="p-2 border border-white/10" colSpan={6}>Няма редове.</td></tr>}
                  {previewRows.map((r, i) => {
                    const weak = !r.matchId;
                    const rowTint = weak ? "bg-yellow-200 text-yellow-900" : (r.score < 55 ? "bg-amber-100 text-amber-900" : "");
                    return (
                      <tr key={`${r.date}-${r.time}-${i}`} className={`even:bg-white/5 ${rowTint}`}>
                        <td className="p-2 border border-white/10 whitespace-nowrap">
                          <input
                            type="date"
                            className="border rounded-lg p-1 text-slate-900"
                            value={r.date}
                            onChange={(e) => setPreviewRows((prev)=>{const c=[...prev]; c[i]={...c[i],date:e.target.value}; return c;})}
                          />
                        </td>
                        <td className="p-2 border border-white/10 whitespace-nowrap">
                          <input
                            type="time"
                            className="border rounded-lg p-1 text-slate-900"
                            value={r.time}
                            onChange={(e) => setPreviewRows((prev)=>{const c=[...prev]; c[i]={...c[i],time:e.target.value}; return c;})}
                          />
                        </td>
                        <td className="p-2 border border-white/10">{r.original}</td>
                        <td className="p-2 border border-white/10">
                          <select
                            className="border rounded-lg p-1 min-w-[260px] text-slate-900"
                            value={r.matchName}
                            onChange={(e) => changeMatch(i, e.target.value)}
                          >
                            <option value="__new__">➕ Ново заглавие</option>
                            {movies
                              .slice()
                              .sort((a,b)=>a.MOVIE_NAME.localeCompare(b.MOVIE_NAME,"bg"))
                              .map((m)=>(
                                <option key={m.MOVIE_ID} value={m.MOVIE_NAME}>{m.MOVIE_NAME}</option>
                              ))}
                          </select>
                        </td>
                        <td className="p-2 border border-white/10 text-center">{r.matchId ? Math.round(r.score) : "—"}</td>
                        <td className="p-2 border border-white/10">
                          {r.matchId ? (
                            <span className={`px-2 py-0.5 rounded text-xs ${r.score >= 70 ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                              {r.score >= 70 ? "Добро съвпадение" : "Провери"}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-yellow-300 text-yellow-900">Изберете филм</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Налични прожекции */}
        {showtimes.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-white/15 bg-white/5">
            <table className="w-full text-sm">
              <thead className="bg-white/10 text-white">
                <tr>
                  <th className="p-2 border border-white/10">Зала</th>
                  <th className="p-2 border border-white/10">Дата</th>
                  <th className="p-2 border border-white/10">Час</th>
                  <th className="p-2 border border-white/10">Тип</th>
                  <th className="p-2 border border-white/10">Цена</th>
                  <th className="p-2 border border-white/10">💾</th>
                  <th className="p-2 border border-white/10">❌</th>
                </tr>
              </thead>

              <tbody className="text-white">
                {showtimes
                  .filter((s) => !filterDate || (s.show_date || "").startsWith(filterDate))
                  .map((s) => {
                    const id = String(s.showtime_id);
                    const e = metaEdits[id] ?? {
                      format_type: s.format_type || "",
                      is_hfr: Number(s.is_hfr) ? 1 : 0,
                      has_dub: String(s.sub_type || "").toUpperCase() === "DUB" || String(s.audio_lang || "").toUpperCase() === "BG",
                      has_sub: String(s.sub_type || "").toUpperCase() === "SUB" || String(s.sub_lang || "").toUpperCase() === "BG" || Number(s.is_ocap) === 1,
                      is_ocap: Number(s.is_ocap) ? 1 : 0,
                      import_price: (s.import_price ?? "") === null ? "" : String(s.import_price ?? "")
                    };

                    return (
                      <tr key={s.showtime_id} className="even:bg-white/5">
                        <td className="border border-white/10 p-2">{s.hall_name}</td>
                        <td className="border border-white/10 p-2">{(s.show_date || "").split("T")[0]}</td>
                        <td className="border border-white/10 p-2">{(s.show_time || "").slice(0,5)}</td>

                        {/* Type editor */}
                        <td className="border border-white/10 p-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className={`px-2 py-1 rounded-xl ${INPUT_LIGHT}`}
                              value={e.format_type}
                              onChange={(ev) => updateMetaEdit(s, { format_type: ev.target.value })}
                            >
                              <option value="">—</option>
                              <option value="2D">2D</option>
                              <option value="3D">3D</option>
                            </select>

                            <label className="inline-flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={!!e.is_hfr}
                                onChange={(ev) => updateMetaEdit(s, { is_hfr: ev.target.checked ? 1 : 0 })}
                              />
                              HFR
                            </label>

                            <label className="inline-flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={!!e.has_sub}
                                onChange={(ev) => {
                                  const checked = ev.target.checked;
                                  updateMetaEdit(s, { has_sub: checked ? 1 : 0, is_ocap: checked ? (e.is_ocap ? 1 : 0) : 0 });
                                }}
                              />
                              СУБ 🇧🇬
                            </label>

                            <label className="inline-flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={!!e.has_dub}
                                onChange={(ev) => updateMetaEdit(s, { has_dub: ev.target.checked ? 1 : 0 })}
                              />
                              ДУБ 🇧🇬
                            </label>

                            <label className="inline-flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={!!e.is_ocap}
                                disabled={!e.has_sub}
                                onChange={(ev) => {
                                  const checked = ev.target.checked;
                                  updateMetaEdit(s, { is_ocap: checked ? 1 : 0, has_sub: 1 });
                                }}
                              />
                              OCAP
                            </label>
                          </div>

                          <div className="text-xs text-white/70 mt-1">
                            {[
                              e.format_type || null,
                              e.is_hfr ? "HFR" : null,
                              e.has_dub ? "ДУБ 🇧🇬" : null,
                              e.has_sub ? `${e.is_ocap ? "OCAP " : ""}СУБ 🇧🇬` : null,
                            ].filter(Boolean).join(" • ") || "—"}
                          </div>
                        </td>

                        {/* Price editor */}
                        <td className="border border-white/10 p-2">
                          <input
                            className={`w-24 px-2 py-1 rounded-xl ${INPUT_LIGHT}`}
                            placeholder="напр. 15"
                            value={e.import_price}
                            onChange={(ev) => updateMetaEdit(s, { import_price: ev.target.value })}
                          />
                        </td>

                        {/* Save */}
                        <td className="border border-white/10 p-2 text-center">
                          <button
                            onClick={() => saveShowtimeMeta(s)}
                            disabled={metaSavingId === id}
                            className="px-3 py-1 rounded-xl bg-emerald-600 text-white disabled:opacity-60"
                            title="Запази"
                          >
                            {metaSavingId === id ? "⏳" : "💾"}
                          </button>
                        </td>

                        {/* Delete */}
                        <td className="border border-white/10 p-2 text-center">
                          <button
                            onClick={async () => {
                              if (!window.confirm("Да изтрия ли прожекцията?")) return;
                              try {
                                await getJSON(`/api/showtimes/${s.showtime_id}`, { method:"DELETE" });
                                await refreshShowtimes();
                              } catch {
                                alert("❌ Грешка при изтриване.");
                              }
                            }}
                            className="px-2 py-1 rounded-lg bg-rose-600 text-white"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
